import {
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Grid,
  InputAdornment,
  makeStyles,
  TextField,
  Typography
} from '@material-ui/core';
import { ArrowDropDown } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import Alert from '@material-ui/lab/Alert';
import { Formik, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { RoleAssignmentMode } from '~/types';
import { DateField, Hours, LoadingButton } from '~/modules/common/components';
import {
  useDialogState,
  useKeyValueSettingsMutation,
  updateCache
} from '~/modules/common/hooks';
import {
  ProjectTeamMemberDropdown,
  ResourceUsersDropdown
} from '~/modules/tasks/components';
import {
  useRemainingProjectAllocationHours,
  useTotalResourceAvailableDurationForDateRange
} from '~/modules/tasks/hooks';
import { mapRepliconDateToUtcObject } from '~/modules/common/dates/convert';
import { getError, hasError } from '~/util';
import { roundToDecimals } from '~/modules/resourcing/common/util/scheduleUtil';
import RoleAssignmentDialog from '~/modules/tasks/components/RoleAssignmentDialog';
import { ADD_TASK_RESOURCE_ESTIMATE_DIALOG_SETTING } from './constants';
import useAddResourceEstimateDialogChangeHandlers from './useAddResourceEstimateDialogChangeHandlers';
import { validationSchema } from './useAddResourceEstimateDialogFormState';
import useResourceEstimateSubmit from './useResourceEstimateSubmit';

const READONLY_INPUT_PROPS = { readOnly: true };

const useDateFieldInputProps = (ariaLabel, className) =>
  useMemo(
    () => ({
      inputProps: { 'aria-label': ariaLabel },
      endAdornment: (
        <InputAdornment className={className} position="end">
          <ArrowDropDown />
        </InputAdornment>
      )
    }),
    [ariaLabel, className]
  );

const useStyles = makeStyles(theme => ({
  actions: {
    paddingRight: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    paddingTop: theme.spacing(1)
  },
  roleDisplayField: {
    marginBottom: theme.spacing(2),
    cursor: 'pointer'
  },
  checkboxEnabled: {
    marginRight: theme.spacing(0),
    marginBottom: theme.spacing(1)
  },
  checkboxDisabled: {
    marginRight: theme.spacing(0),
    marginBottom: theme.spacing(2)
  },
  dateContainer: {
    marginBottom: theme.spacing(1)
  },
  dateIcon: {
    marginRight: theme.spacing(-0.875),
    padding: theme.spacing(0.25),
    color: theme.palette.action.active
  },
  numberInputWithValue: {
    marginBottom: theme.spacing(2),
    left: 0,
    right: 'unset',
    transformOrigin: 'left top'
  },
  numberInputWithoutValue: {
    marginBottom: theme.spacing(2),
    right: theme.spacing(3),
    left: 0,
    transformOrigin: 'left top',
    '&.Mui-focused': {
      right: 'unset',
      left: 0,
      transformOrigin: 'left top'
    }
  },
  warning: {
    marginTop: theme.spacing(2)
  }
}));

const dialogFieldIds = {
  estimatedHours: 'AddResourceEstimateDialogEstimatedHoursField',
  checkbox: 'AddResourceEstimateDialogCheckboxField',
  startDate: 'AddResourceEstimateDialogStartDateField',
  endDate: 'AddResourceEstimateDialogEndDateField'
};

const getRoleAssignmentDisplayText = ({ mode, roles, formatMessage }) => {
  if (mode === RoleAssignmentMode.Inherit)
    return formatMessage({ id: 'selectTaskRolesDialog.taskRoles' });
  if (mode === RoleAssignmentMode.UserProfileRoles)
    return formatMessage({ id: 'selectTaskRolesDialog.userProfileRoles' });
  if (!roles || roles.length === 0)
    return formatMessage({ id: 'selectTaskRolesDialog.noSelectedRoles' });
  const defaultRole = roles.find(r => r.isDefault);
  const extra = roles.length - 1;
  const defaultLabel =
    defaultRole?.displayText ??
    formatMessage({ id: 'selectTaskRolesDialog.noDefaultRole' });

  return extra > 0 ? `${defaultLabel} +${extra}` : defaultLabel;
};

const ResourceForm = ({
  task,
  onClose,
  isEditMode,
  isFullVersionEnabled,
  setIsDirty
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles();

  const {
    errors,
    values,
    setFieldValue,
    setValues,
    dirty,
    initialValues,
    validateForm,
    isSubmitting,
    submitForm
  } = useFormikContext();

  const {
    role,
    resource,
    roleAssignment,
    initialEstimatedHours,
    isCreateTaskAllocationChecked,
    startDate,
    endDate
  } = values;

  const {
    id: taskId,
    projectReference: { id: projectId }
  } = task;

  const {
    open: isRolesDialogOpen,
    openDialog: openRolesDialog,
    closeDialog: closeRolesDialog
  } = useDialogState();

  const [putKeyValueSettings] = useKeyValueSettingsMutation(
    updateCache(ADD_TASK_RESOURCE_ESTIMATE_DIALOG_SETTING)
  );

  const {
    onInitialEstimatedHoursChange,
    onCreateTaskAllocationChange,
    onStartDateChange,
    onEndDateChange,
    onResourceChange
  } = useAddResourceEstimateDialogChangeHandlers({
    values,
    setValues,
    setFieldValue,
    putKeyValueSettings
  });

  const handleRoleAssignmentSave = useCallback(
    ({ roleAssignmentMode, specificRoles }) => {
      const defaultRoleEntry = specificRoles.find(r => r.isDefault);

      setFieldValue('roleAssignment', {
        mode: roleAssignmentMode,
        roles: specificRoles
      });
      const hasDefaultRole =
        roleAssignmentMode === RoleAssignmentMode.SpecificRoles &&
        defaultRoleEntry;

      setFieldValue(
        'role',
        hasDefaultRole
          ? {
              id: defaultRoleEntry.roleUri,
              displayText: defaultRoleEntry.displayText ?? ''
            }
          : null
      );
    },
    [setFieldValue]
  );

  useEffect(() => {
    setIsDirty(dirty);
  }, [dirty, setIsDirty]);

  useEffect(() => {
    validateForm(initialValues);
  }, [validateForm, initialValues]);

  const skipAvailableHoursLoading = !(
    isCreateTaskAllocationChecked &&
    resource?.id &&
    startDate &&
    endDate
  );

  const {
    availableDuration,
    loading: availabilityLoading
  } = useTotalResourceAvailableDurationForDateRange({
    resourceUserId: resource?.id,
    startDate,
    endDate,
    skip: skipAvailableHoursLoading
  });

  const {
    remainingProjectAllocationHours,
    loading: isRemainingProjectAllocationHoursLoading
  } = useRemainingProjectAllocationHours({
    userId: resource?.id,
    projectId,
    startDate: startDate && mapRepliconDateToUtcObject(startDate),
    endDate: endDate && mapRepliconDateToUtcObject(endDate),
    skip: skipAvailableHoursLoading
  });

  const hasStartDateError = hasError(errors, 'startDate');
  const hasEndDateError = hasError(errors, 'endDate');
  const hasInitialEstimatedHoursError = hasError(
    errors,
    'initialEstimatedHours'
  );

  const showOverAllocationBanner =
    isCreateTaskAllocationChecked &&
    initialEstimatedHours >
      roundToDecimals(availableDuration + remainingProjectAllocationHours);

  const roleDisplayText = getRoleAssignmentDisplayText({
    mode: roleAssignment?.mode,
    roles: roleAssignment?.roles,
    formatMessage
  });

  const estimatedHoursLabel = formatMessage({
    id: 'addResourceEstimateDialog.estimatedHours'
  });
  const resourceLabel = formatMessage({
    id: 'addResourceEstimateDialog.resource'
  });
  const startDateLabel = formatMessage({
    id: 'addResourceEstimateDialog.startDate'
  });
  const endDateLabel = formatMessage({
    id: 'addResourceEstimateDialog.endDate'
  });

  const startDateInputProps = useDateFieldInputProps(
    startDateLabel,
    classes.dateIcon
  );
  const endDateInputProps = useDateFieldInputProps(
    endDateLabel,
    classes.dateIcon
  );

  const isDisabled =
    !resource ||
    (!dirty && isEditMode) ||
    Object.keys(errors).length > 0 ||
    isSubmitting;

  return (
    <>
      <DialogContent>
        <TextField
          fullWidth
          variant="outlined"
          label={formatMessage({ id: 'addResourceEstimateDialog.role' })}
          value={roleDisplayText}
          className={classes.roleDisplayField}
          onClick={openRolesDialog}
          InputProps={READONLY_INPUT_PROPS}
        />
        <RoleAssignmentDialog
          open={isRolesDialogOpen}
          onClose={closeRolesDialog}
          onSave={handleRoleAssignmentSave}
          taskUri={taskId}
          teamMemberUserId={resource?.id}
        />
        <Hours
          className={
            initialEstimatedHours
              ? classes.numberInputWithValue
              : classes.numberInputWithoutValue
          }
          variant="outlined"
          data-qe-id={dialogFieldIds.estimatedHours}
          id={dialogFieldIds.estimatedHours}
          label={estimatedHoursLabel}
          ariaLabel={estimatedHoursLabel}
          margin="none"
          isEditible
          align="left"
          onChange={onInitialEstimatedHoursChange}
          value={initialEstimatedHours}
          precision={2}
          error={hasInitialEstimatedHoursError}
          helperText={getError(errors, 'initialEstimatedHours')}
        />
        {isFullVersionEnabled && (
          <>
            <FormControlLabel
              className={
                isCreateTaskAllocationChecked
                  ? classes.checkboxEnabled
                  : classes.checkboxDisabled
              }
              control={
                <Checkbox
                  data-qe-id={dialogFieldIds.checkbox}
                  checked={isCreateTaskAllocationChecked}
                  onChange={onCreateTaskAllocationChange}
                  color="primary"
                />
              }
              label={
                <Typography variant="body1">
                  {formatMessage({
                    id: 'addResourceEstimateDialog.allocateResourceTimeCheckBox'
                  })}
                </Typography>
              }
              labelPlacement="end"
            />
            {isCreateTaskAllocationChecked && (
              <Grid container spacing={2} className={classes.dateContainer}>
                <Grid item xs={6}>
                  <DateField
                    required
                    data-qe-id={dialogFieldIds.startDate}
                    id={dialogFieldIds.startDate}
                    editable
                    label={startDateLabel}
                    value={startDate}
                    error={hasStartDateError}
                    helperText={getError(errors, 'startDate')}
                    onChange={onStartDateChange}
                    variant="outlined"
                    InputProps={startDateInputProps}
                  />
                </Grid>
                <Grid item xs={6}>
                  <DateField
                    required
                    data-qe-id={dialogFieldIds.endDate}
                    id={dialogFieldIds.endDate}
                    editable
                    label={endDateLabel}
                    value={endDate}
                    error={hasEndDateError}
                    helperText={getError(errors, 'endDate')}
                    onChange={onEndDateChange}
                    variant="outlined"
                    InputProps={endDateInputProps}
                  />
                </Grid>
              </Grid>
            )}
          </>
        )}
        {isFullVersionEnabled ? (
          <ResourceUsersDropdown
            disabled={
              hasStartDateError ||
              hasEndDateError ||
              hasInitialEstimatedHoursError
            }
            assignedRole={role}
            onResourceChange={onResourceChange}
            projectId={projectId}
            value={resource}
            placeholder={resourceLabel}
            ariaLabel={resourceLabel}
            variant="outlined"
            label={resourceLabel}
            taskId={taskId}
            startDate={startDate}
            endDate={endDate}
            initialEstimatedHours={initialEstimatedHours}
            hideAvailability={!isCreateTaskAllocationChecked}
          />
        ) : (
          <ProjectTeamMemberDropdown
            assignedRole={role}
            onResourceChange={onResourceChange}
            projectId={projectId}
            value={resource}
            placeholder={resourceLabel}
            ariaLabel={resourceLabel}
            variant="outlined"
            label={resourceLabel}
            taskId={taskId}
          />
        )}
        {availabilityLoading || isRemainingProjectAllocationHoursLoading ? (
          <Skeleton height={70} width="100%" />
        ) : (
          showOverAllocationBanner && (
            <Alert severity="warning" icon={false} className={classes.warning}>
              <FormattedMessage id="addResourceEstimateDialog.messages.noEnoughAvailableHoursWarning" />
            </Alert>
          )
        )}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button onClick={onClose} variant="contained">
          {formatMessage({ id: 'button.cancel' })}
        </Button>
        <LoadingButton
          color="primary"
          onClick={submitForm}
          variant="contained"
          disabled={isDisabled}
          isLoading={isSubmitting}
        >
          {formatMessage({
            id: isEditMode
              ? 'editResourceEstimateDialog.setResource'
              : 'addResourceEstimateDialog.addResource'
          })}
        </LoadingButton>
      </DialogActions>
    </>
  );
};

ResourceForm.propTypes = {
  task: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool.isRequired,
  isFullVersionEnabled: PropTypes.bool.isRequired,
  setIsDirty: PropTypes.func.isRequired
};

const ResourceFormContent = ({
  task,
  onClose,
  isEditMode,
  isFullVersionEnabled,
  setIsDirty,
  computedInitialHours,
  resourceEstimate,
  resourceEstimates,
  setParentFieldValue,
  canViewCost,
  isCreateTaskAllocationEnabled
}) => {
  const intl = useIntl();

  const { onResourceEstimateSubmit } = useResourceEstimateSubmit({
    task,
    canViewCost,
    resourceEstimates,
    setParentFieldValue,
    onClose
  });

  const initialValues = useMemo(
    () => ({
      role: isEditMode
        ? resourceEstimate.projectRole || null
        : task.assignedRole || null,
      resource: isEditMode ? resourceEstimate.resourceUser || null : null,
      roleAssignment: { mode: RoleAssignmentMode.Inherit, roles: [] },
      initialEstimatedHours: computedInitialHours,
      isCreateTaskAllocationChecked: isFullVersionEnabled
        ? isCreateTaskAllocationEnabled ?? true
        : false,
      startDate: task.startDate || null,
      endDate: task.endDate || null,
      taskResourceEstimateId: isEditMode
        ? resourceEstimate.taskResourceEstimateId
        : null
    }),
    [
      isEditMode,
      resourceEstimate,
      task,
      computedInitialHours,
      isFullVersionEnabled,
      isCreateTaskAllocationEnabled
    ]
  );

  const resourceValidationSchema = useMemo(() => validationSchema({ intl }), [
    intl
  ]);

  const handleSubmit = useCallback(
    async (
      {
        role,
        resource,
        roleAssignment,
        initialEstimatedHours,
        isCreateTaskAllocationChecked,
        startDate,
        endDate,
        taskResourceEstimateId
      },
      { setSubmitting }
    ) => {
      const resourceEstimateInput = {
        resourceUserId: resource?.id,
        projectRoleId: role?.id,
        initialEstimatedHours,
        taskResourceEstimateId
      };

      if (
        isCreateTaskAllocationChecked &&
        resource?.id &&
        initialEstimatedHours
      ) {
        resourceEstimateInput.taskResourceEstimateAllocationInput = {
          projectId: task.projectReference?.id,
          startDate,
          endDate,
          allocatedHours: initialEstimatedHours
        };
      }

      if (resource?.id && roleAssignment?.mode) {
        resourceEstimateInput.taskResourceRoleAssignmentInput = {
          roleAssignmentMode: roleAssignment.mode,
          specificRolesToBeAssigned: (
            roleAssignment.roles ?? []
          ).map(({ roleUri, isDefault }) => ({ roleUri, isDefault }))
        };
      }

      await onResourceEstimateSubmit({
        input: resourceEstimateInput,
        resource,
        role,
        setSubmitting
      });
    },
    [onResourceEstimateSubmit, task]
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={resourceValidationSchema}
      onSubmit={handleSubmit}
    >
      <ResourceForm
        task={task}
        onClose={onClose}
        isEditMode={isEditMode}
        isFullVersionEnabled={isFullVersionEnabled}
        setIsDirty={setIsDirty}
      />
    </Formik>
  );
};

ResourceFormContent.propTypes = {
  task: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool.isRequired,
  isFullVersionEnabled: PropTypes.bool.isRequired,
  setIsDirty: PropTypes.func.isRequired,
  computedInitialHours: PropTypes.number,
  resourceEstimate: PropTypes.object,
  resourceEstimates: PropTypes.array.isRequired,
  setParentFieldValue: PropTypes.func.isRequired,
  canViewCost: PropTypes.bool.isRequired,
  isCreateTaskAllocationEnabled: PropTypes.bool
};

export default ResourceFormContent;
